import { useEffect, useState } from "react";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { enqueueSnackbar } from "notistack";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  createAttachment,
  updateStatusCloseDetails,
} from "../../../../../apis";
import Icon from "../../../../Icon/Icon";
import { EGInput } from "../../../../MainComponents/Input/EGInput";
import EGFileUpload from "../../../../MainComponents/FileUpload/EGFileUpload/EGFileUpload";
import { EGButton } from "../../../../MainComponents/Button/EGButton/EGButton";
import { SubmitButtons } from "./CloseReport";
import { CommentFiles } from "./CommentFiles";
import { RelatedReportsClosureSection } from "./RelatedReportsClosureSection";

const Anothercauses = ({ anotherCauses, setAnotherCauses }) => {
  const theme = useTheme();

  useEffect(() => {
    return () => {
      setAnotherCauses([""]);
    };
  }, []);

  const handleChange = (value, idx) => {
    const newCauses = _.cloneDeep(anotherCauses);
    newCauses[idx] = value;
    setAnotherCauses(newCauses);
  };

  const handleAddCause = () => {
    const newCauses = _.cloneDeep(anotherCauses);
    newCauses.push("");
    setAnotherCauses(newCauses);
  };

  return (
    <Stack spacing={2} marginTop={2}>
      {anotherCauses.map((item, idx) => (
        <Stack key={idx} direction="row" alignItems="center">
          <EGInput
            label="Ingresa una nueva causa"
            placeholder="Escribe aquí"
            required={true}
            value={item}
            showHelpText={false}
            showSuccessValidation
            onChange={(e) => handleChange(e.target.value, idx)}
            isValid={item.trim() === "" ? false : true}
            autoComplete="off"
          />
          {idx === anotherCauses.length - 1 && (
            <Tooltip title="Añadir" arrow>
              <Box onClick={handleAddCause} marginLeft={2}>
                <Icon
                  name="add_square"
                  size={24}
                  color={theme.palette.ethicsBlue.main}
                />
              </Box>
            </Tooltip>
          )}
        </Stack>
      ))}
    </Stack>
  );
};

export const ClosedUnfounded = ({
  id,
  reportDetail,
  closeDate,
  reportExpired,
  reportRender,
  editClosed,
  setEditClosed,
}) => {
  const [t] = useTranslation("report");
  const theme = useTheme();

  const [observations, setObservations] = useState(
    reportDetail?.status?.details?.observations || ""
  );
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState(
    reportDetail?.status?.details?.files || []
  );
  const [isLoading, setIsLoading] = useState(false);

  const [causes, setCauses] = useState(
    reportDetail?.status?.details?.causes || []
  );
  const [anotherCauses, setAnotherCauses] = useState(
    reportDetail?.status?.details?.another_causes?.length > 0
      ? reportDetail.status.details.another_causes
      : [""]
  );

  // Estados para cerrar denuncias relacionadas
  const [closeLinkedReports, setCloseLinkedReports] = useState(false);
  const [closeAllReports, setCloseAllReports] = useState(true);
  const [selectedReports, setSelectedReports] = useState([]);

  const returnIsValid = () => {
    if (
      causes?.includes("another") &&
      anotherCauses?.findIndex((item) => item.trim() === "") >= 0
    ) {
      return false;
    }
    return true;
  };

  // Obtener los reportes a cerrar
  const getReportsToClose = () => {
    if (!closeLinkedReports) return [];
    if (closeAllReports) return reportDetail?.related?.map((r) => r.id_report) || [];
    return selectedReports;
  };

  const handleSubmit = async (e) => {
    setIsLoading(true);

    let files_id;
    let filesToSend = [];

    enqueueSnackbar(`${t("Conclusion.sending")}...`, { variant: "info" });

    try {
      if (files.length > 0) {
        for (const fileObj of files) {
          const { formData } = fileObj;
          const filesRes = await createAttachment(formData);
          files_id = filesRes.attachments_id;
          filesToSend.push({
            file: files_id,
            name: fileObj.file.name,
            ...(!!fileObj.comment && { comment: fileObj.comment }),
          });
        }
      }

      // Add current files
      if (uploadedFiles && uploadedFiles.length > 0) {
        uploadedFiles.forEach((file) => {
          filesToSend.push({
            file: file.file,
            name: file.name,
            ...(!!file.comment && { comment: file.comment }),
          });
        });
      }

      const details = {
        another_causes: anotherCauses[0] === "" ? [] : anotherCauses,
        followup: false,
        closed: "unfounded",
        close_date: closeDate,
        causes: causes,
        observations,
        files: filesToSend,
        expired_reports: reportExpired,
      };

      await updateStatusCloseDetails(id, details);

      // Cerrar las denuncias relacionadas si aplica
      const reportsToClose = getReportsToClose();
      if (reportsToClose.length > 0) {
        const closeDetails = {
          closed: "unfounded",
          close_date: closeDate,
          observations,
          files: filesToSend,
          followup: false,
          causes: causes,
          another_causes: anotherCauses[0] === "" ? [] : anotherCauses,
        };

        // Cerrar cada reporte seleccionado
        const closePromises = reportsToClose.map((reportId) =>
          updateStatusCloseDetails(reportId, closeDetails)
        );

        await Promise.all(closePromises);

        enqueueSnackbar(
          `${t("Conclusion.sending_successful")} - ${reportsToClose.length} ${t("Conclusion.related_reports_closed") || "denuncias relacionadas cerradas"}`,
          { variant: "success" }
        );
      } else {
        enqueueSnackbar(`${t("Conclusion.sending_successful")}`, {
          variant: "success",
        });
      }

      reportRender();
      setIsLoading(false);
      setEditClosed(false);
    } catch (error) {
      setIsLoading(false);
      console.error(error);
      enqueueSnackbar(`${t("Conclusion.error_closing_reports") || "Error al cerrar las denuncias"}`, {
        variant: "error",
      });
    }
  };

  const unfoundedOptionsValues = [
    {
      id: "incomplete_information",
      label: t("Conclusion.incomplete_information"),
    },
    { id: "false_information", label: t("Conclusion.false_information") },
    {
      id: "insufficient_evidence_found",
      label: t("Conclusion.insufficient_evidence_found"),
    },
    { id: "another", label: t("Conclusion.another") },
  ];

  return (
    <>
      <Autocomplete
        options={unfoundedOptionsValues}
        getOptionLabel={(option) => option.label}
        id="movie-customized-option-demo"
        disableCloseOnSelect
        disableClearable
        multiple
        value={causes.map((id) =>
          unfoundedOptionsValues.find((opt) => opt.id === id)
        )} 
        onChange={(_, selecteds) => setCauses(selecteds.map((opt) => opt.id))}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              key={option.id}
              sx={{
                backgroundColor: theme.palette.blueGray100.main,
                fontSize: 12,
              }}
              size="small"
              {...getTagProps({ index })}
              label={
                <Typography variant="body1" mx={1}>
                  {option.label}
                </Typography>
              }
            />
          ))
        }
        renderOption={(props, option, { selected }) => {
          return (
            <Box
              key={option.id}
              {...props}
              sx={{
                maxHeight: 47,
                p: 2,
                my: 0.5,
                mx: 1,
                borderRadius: 1.5,
                border: `${theme.palette.blueGray50.main} 1px solid`,
              }}
            >
              <Checkbox size="small" checked={selected} />
              <Typography variant="body1" mx={1}>
                {option.label}
              </Typography>
            </Box>
          );
        }}
        renderInput={(params) => (
          <EGInput
            label={t("Conclusion.unfounded_closed_causes")}
            placeholder={t("Conclusion.select_an_option")}
            {...params}
          />
        )}
      />

      {causes.find((item) => item === "another") && (
        <Anothercauses
          anotherCauses={anotherCauses}
          setAnotherCauses={setAnotherCauses}
        />
      )}

      <EGInput
        label={t("Conclusion.observations_type_close")}
        placeholder={t("Conclusion.observations_type_close_placeholder")}
        multiline
        rows={4}
        value={observations}
        onChange={(e) => setObservations(e.target.value)}
        sx={{ marginY: 2 }}
      />

      <Box marginBottom={2}>
        <EGFileUpload
          title={t("Conclusion.support_fiels")}
          files={files}
          setFiles={setFiles}
        />
      </Box>

      {(files.length > 0 || uploadedFiles.length > 0) && (
        <Stack
          border={`1px solid ${theme.palette.teal.main}`}
          borderRadius={1}
          marginBottom={2}
        >
          {files.length !== 0 && (
            <CommentFiles
              files={files}
              setFiles={setFiles}
              pathName="file.name"
              pathSize="file.size"
            />
          )}

          {uploadedFiles.length !== 0 && (
            <CommentFiles files={uploadedFiles} setFiles={setUploadedFiles} />
          )}
        </Stack>
      )}

      {/* Sección para cerrar denuncias relacionadas */}
      <RelatedReportsClosureSection
        relatedReports={reportDetail?.related || []}
        closeLinkedReports={closeLinkedReports}
        setCloseLinkedReports={setCloseLinkedReports}
        closeAllReports={closeAllReports}
        setCloseAllReports={setCloseAllReports}
        selectedReports={selectedReports}
        setSelectedReports={setSelectedReports}
      />

      <SubmitButtons
        isLoading={isLoading}
        disabledSave={!returnIsValid()}
        onSave={handleSubmit}
        onCancel={() => setEditClosed(false)}
        hideCancel={!editClosed}
      />
    </>
  );
};
